/**
 * MarketMonitorDashboardProvider
 *
 * Owns the active Market Monitor dashboard state:
 *  - dashboard list
 *  - active dashboard (id, name, widgets)
 *  - dirty / saving state for the manual-save UX (Q8)
 *  - widget-grid actions (add/remove/move) backed by gridDashboardReducer
 *  - chart controller scoped per chart-widget instanceId, exposed via
 *    ChartControllerProviderContext
 *
 * Amendment A (2026-05-02): each chart is its own widget; the chart-row
 * controller is gone. `addChartWidgetAt(x, y, seedDsl?)` (used by the
 * canvas-drop handler) is implemented in Phase A3.
 *
 * Persistence is HTTP via dashboardsApi (replaces the localStorage path
 * used by the Trading layout).
 */

import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuth } from '../../../contexts/AuthContext';
import logger from '../../../logging/logger';
import {
  gridDashboardReducer,
  type WidgetPlacement,
} from '../../../components/grid-dashboard';
import {
  CHART_WIDGET_TYPE,
  ChartControllerProviderContext,
  EXCEL_WIDGET_TYPE,
  ExcelControllerProviderContext,
  applyCellPatch as applyExcelCellPatch,
  appendCol,
  appendRow,
  clearCells as clearExcelCells,
  composeCellDsl,
  createInitialChartConfig,
  createInitialExcelConfig,
  dropCol,
  dropRow,
  growGrid as growExcelGrid,
  isChartConfig,
  isDslCell,
  isExcelConfig,
  marketMonitorWidgetRegistry,
  parseFormulaBody,
  promptOf,
  setCellRaw as setExcelCellRaw,
  setCellResolvedDsl as setExcelCellResolvedDsl,
  setCellsConditionalFormatting as setExcelCellsConditionalFormatting,
  setCellsFormat as setExcelCellsFormat,
  setCellsStyle as setExcelCellsStyle,
  writeSheetCells as writeExcelSheetCells,
  type ChartController,
  type ChartControllerProvider,
  type ChartWidgetConfig,
  type ExcelController,
  type ExcelControllerProvider,
  type ExcelWidgetConfig,
} from '../widgets';
import type { ChartPeriod } from '../../../pages/dslAgentTest/chartPeriod';
import type { DslTag } from '../../../pages/dslAgentTest/types';
import {
  type DashboardSummary,
  type MonitorDashboard,
  addWidget as addWidgetApi,
  createDashboard,
  deleteDashboard,
  getDashboard,
  listDashboards,
  removeWidget as removeWidgetApi,
  renameDashboard,
  updateWidget,
  widgetsToApi,
} from './dashboardsApi';

const ACTIVE_ID_STORAGE_KEY = 'market-monitor:active-id';

/**
 * Q13 collision-resolution: starting at (x, y), bump y down one row at a
 * time until a `(w, h)` placement does not overlap any existing widget.
 * Returns the chosen y.
 */
function bumpDownOnCollision(
  existing: WidgetPlacement[],
  x: number,
  startY: number,
  w: number,
  h: number,
): number {
  const collides = (candY: number): boolean =>
    existing.some(
      (other) =>
        x < other.x + other.w &&
        x + w > other.x &&
        candY < other.y + other.h &&
        candY + h > other.y,
    );
  let y = startY;
  while (collides(y)) y += 1;
  return y;
}

/**
 * DSL to seed a chart with for one excel cell. Prefers the composed
 * expression (references inlined). A cell whose translation has not
 * landed yet falls back to its prompt so the chart widget can translate
 * it itself — but only when it has no references, since English mixed
 * with unresolved `@refs` is not something the chart can run.
 */
function chartSeedFor(
  config: ExcelWidgetConfig,
  r: number,
  c: number,
): string | null {
  const composed = composeCellDsl(config, r, c);
  if (composed.kind === 'ok') return composed.dsl;
  if (composed.kind === 'error') return null;

  const parsed = parseFormulaBody(promptOf(config.cells[r][c]));
  if (parsed.kind === 'error' || parsed.parsed.refs.length > 0) return null;
  return promptOf(config.cells[r][c]);
}

export interface MarketMonitorDashboardState {
  // dashboard list
  list: DashboardSummary[];
  isListLoading: boolean;
  // active dashboard
  activeId: string | null;
  activeName: string | null;
  widgets: WidgetPlacement[];
  // status
  isLoadingActive: boolean;
  isSaving: boolean;
  isDirty: boolean;
  error: string | null;
  // dashboard CRUD
  refresh: () => Promise<void>;
  open: (id: string) => Promise<void>;
  newDashboard: () => void;
  saveAs: (name: string) => Promise<MonitorDashboard | null>;
  save: () => Promise<MonitorDashboard | null>;
  rename: (id: string, name: string) => Promise<void>;
  remove: (id: string) => Promise<boolean>;
  // widget grid actions
  addWidget: (widgetType: string) => void;
  removeWidget: (instanceId: string) => void;
  updateLayout: (widgets: WidgetPlacement[]) => void;
  /**
   * Place a chart widget at a specific grid cell (Q13). On collision the
   * y is bumped down one row at a time until the placement is free. Used
   * by the canvas-drop handler (legend drag-drop) and by the Excel Chart
   * toolbar action (one tag per DSL cell in the selection).
   */
  addChartWidgetAt: (args: { x: number; y: number; seedDsls?: string[] }) => void;
}

const MarketMonitorDashboardContext = createContext<MarketMonitorDashboardState | undefined>(
  undefined,
);

const errorMessage = (caught: unknown, fallback: string): string => {
  if (caught instanceof Error) return caught.message;
  return fallback;
};

const serializeWidgets = (widgets: WidgetPlacement[]): string =>
  JSON.stringify(widgetsToApi(widgets));

const readStoredActiveId = (): string | null => {
  try {
    return window.localStorage.getItem(ACTIVE_ID_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredActiveId = (value: string | null): void => {
  try {
    if (value === null) {
      window.localStorage.removeItem(ACTIVE_ID_STORAGE_KEY);
    } else {
      window.localStorage.setItem(ACTIVE_ID_STORAGE_KEY, value);
    }
  } catch {
    // localStorage may be unavailable; treat as no-op.
  }
};

interface ProviderProps {
  children: ReactNode;
}

export const MarketMonitorDashboardProvider: React.FC<ProviderProps> = ({ children }) => {
  const { userUuid, getToken } = useAuth();

  const [list, setList] = useState<DashboardSummary[]>([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeName, setActiveName] = useState<string | null>(null);
  const [docVersion, setDocVersion] = useState<number>(0);
  const [widgets, setWidgets] = useState<WidgetPlacement[]>([]);
  const [savedSnapshot, setSavedSnapshot] = useState<string>(() => serializeWidgets([]));
  const [isLoadingActive, setIsLoadingActive] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestSeqRef = useRef<number>(0);
  const restoreAttemptedRef = useRef<boolean>(false);
  const widgetsRef = useRef<WidgetPlacement[]>([]);
  useEffect(() => {
    widgetsRef.current = widgets;
  }, [widgets]);

  const fetchList = useCallback(async () => {
    if (!userUuid) return;
    setIsListLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const summaries = await listDashboards(userUuid, token);
      setList(summaries);
    } catch (caught: unknown) {
      const message = errorMessage(caught, 'Failed to load dashboards.');
      setError(message);
      logger.error(`MarketMonitorDashboardProvider: list failed — ${message}`);
    } finally {
      setIsListLoading(false);
    }
  }, [userUuid, getToken]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const adoptDashboard = useCallback((dashboard: MonitorDashboard) => {
    setActiveId(dashboard.id);
    setActiveName(dashboard.name);
    setDocVersion(dashboard.docVersion);
    setWidgets(dashboard.widgets);
    setSavedSnapshot(serializeWidgets(dashboard.widgets));
  }, []);

  const open = useCallback(
    async (id: string) => {
      if (!userUuid) return;
      const seq = ++requestSeqRef.current;
      setIsLoadingActive(true);
      setError(null);
      try {
        const token = await getToken();
        const dashboard = await getDashboard(userUuid, token, id);
        if (seq !== requestSeqRef.current) return;
        adoptDashboard(dashboard);
        writeStoredActiveId(dashboard.id);
      } catch (caught: unknown) {
        const message = errorMessage(caught, 'Failed to open dashboard.');
        setError(message);
        logger.error(`MarketMonitorDashboardProvider: open ${id} failed — ${message}`);
      } finally {
        if (seq === requestSeqRef.current) setIsLoadingActive(false);
      }
    },
    [userUuid, getToken, adoptDashboard],
  );

  useEffect(() => {
    if (restoreAttemptedRef.current) return;
    if (!userUuid) return;
    if (isListLoading) return;
    restoreAttemptedRef.current = true;
    const stored = readStoredActiveId();
    if (!stored) return;
    if (!list.some((s) => s.id === stored)) {
      writeStoredActiveId(null);
      return;
    }
    void open(stored);
  }, [userUuid, isListLoading, list, open]);

  const newDashboard = useCallback(() => {
    requestSeqRef.current += 1;
    setActiveId(null);
    setActiveName(null);
    setDocVersion(0);
    setWidgets([]);
    setSavedSnapshot(serializeWidgets([]));
    setError(null);
    writeStoredActiveId(null);
  }, []);

  const saveAs = useCallback(
    async (name: string): Promise<MonitorDashboard | null> => {
      if (!userUuid) return null;
      const trimmed = name.trim();
      if (!trimmed) {
        setError('Dashboard name cannot be empty.');
        return null;
      }
      setIsSaving(true);
      setError(null);
      try {
        const token = await getToken();
        const dashboard = await createDashboard(userUuid, token, trimmed, widgets);
        adoptDashboard(dashboard);
        writeStoredActiveId(dashboard.id);
        await fetchList();
        return dashboard;
      } catch (caught: unknown) {
        const message = errorMessage(caught, 'Failed to save dashboard.');
        setError(message);
        logger.error(`MarketMonitorDashboardProvider: saveAs failed — ${message}`);
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [userUuid, getToken, widgets, adoptDashboard, fetchList],
  );

  const save = useCallback(async (): Promise<MonitorDashboard | null> => {
    if (!userUuid) return null;
    if (!activeId) {
      setError('No dashboard selected. Use "Save as" to create one first.');
      return null;
    }
    setIsSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const serverState = await getDashboard(userUuid, token, activeId);
      const serverWidgets = new Map<string, WidgetPlacement>(
        serverState.widgets.map((w) => [w.instanceId, w]),
      );
      const clientIds = new Set(widgets.map((w) => w.instanceId));
      let docVer = serverState.docVersion;

      for (const widget of widgets) {
        if (!serverWidgets.has(widget.instanceId)) {
          const updated = await addWidgetApi(
            userUuid,
            token,
            activeId,
            docVer,
            widget,
          );
          docVer = updated.docVersion;
        }
      }

      for (const widget of widgets) {
        const onServer = serverWidgets.get(widget.instanceId);
        if (onServer) {
          await updateWidget(
            userUuid,
            token,
            activeId,
            widget.instanceId,
            onServer.version ?? 0,
            widget,
          );
        }
      }

      for (const sw of serverState.widgets) {
        if (!clientIds.has(sw.instanceId)) {
          const updated = await removeWidgetApi(
            userUuid,
            token,
            activeId,
            sw.instanceId,
            docVer,
          );
          docVer = updated.docVersion;
        }
      }

      const fresh = await getDashboard(userUuid, token, activeId);
      adoptDashboard(fresh);
      await fetchList();
      return fresh;
    } catch (caught: unknown) {
      const status = (caught as { status?: number })?.status;
      if (status === 409) {
        setError(
          'Dashboard was changed in another tab — reloading from server.',
        );
        try {
          const token = await getToken();
          const fresh = await getDashboard(userUuid, token, activeId);
          adoptDashboard(fresh);
        } catch {
          // fall through to surfaced error
        }
        return null;
      }
      const message = errorMessage(caught, 'Failed to save dashboard.');
      setError(message);
      logger.error(`MarketMonitorDashboardProvider: save failed — ${message}`);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [userUuid, getToken, activeId, widgets, adoptDashboard, fetchList]);

  const rename = useCallback(
    async (id: string, name: string) => {
      if (!userUuid) return;
      const trimmed = name.trim();
      if (!trimmed) {
        setError('Dashboard name cannot be empty.');
        return;
      }
      setError(null);
      try {
        const token = await getToken();
        const expected = id === activeId ? docVersion : 0;
        const dashboard =
          id === activeId
            ? await renameDashboard(userUuid, token, id, expected, trimmed)
            : await (async () => {
                const current = await getDashboard(userUuid, token, id);
                return renameDashboard(
                  userUuid,
                  token,
                  id,
                  current.docVersion,
                  trimmed,
                );
              })();
        if (id === activeId) {
          setActiveName(dashboard.name);
          setDocVersion(dashboard.docVersion);
        }
        await fetchList();
      } catch (caught: unknown) {
        const message = errorMessage(caught, 'Failed to rename dashboard.');
        setError(message);
        logger.error(`MarketMonitorDashboardProvider: rename ${id} failed — ${message}`);
      }
    },
    [userUuid, getToken, activeId, docVersion, fetchList],
  );

  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!userUuid) return false;
      setError(null);
      try {
        const token = await getToken();
        await deleteDashboard(userUuid, token, id);
        if (id === activeId) {
          setActiveId(null);
          setActiveName(null);
          setWidgets([]);
          setSavedSnapshot(serializeWidgets([]));
          writeStoredActiveId(null);
        }
        await fetchList();
        return true;
      } catch (caught: unknown) {
        const message = errorMessage(caught, 'Failed to delete dashboard.');
        setError(message);
        logger.error(`MarketMonitorDashboardProvider: delete ${id} failed — ${message}`);
        return false;
      }
    },
    [userUuid, getToken, activeId, fetchList],
  );

  const addWidget = useCallback((widgetType: string) => {
    let initialConfig: Record<string, unknown> | undefined;
    if (widgetType === CHART_WIDGET_TYPE) {
      initialConfig = createInitialChartConfig() as unknown as Record<string, unknown>;
    } else if (widgetType === EXCEL_WIDGET_TYPE) {
      initialConfig = createInitialExcelConfig() as unknown as Record<string, unknown>;
    }
    setWidgets((prev) => {
      const next = gridDashboardReducer(
        { widgets: prev },
        { type: 'ADD_WIDGET', payload: { widgetType, config: initialConfig } },
        marketMonitorWidgetRegistry,
      );
      return next.widgets;
    });
  }, []);

  const removeWidget = useCallback((instanceId: string) => {
    setWidgets((prev) => {
      const next = gridDashboardReducer(
        { widgets: prev },
        { type: 'REMOVE_WIDGET', payload: { instanceId } },
        marketMonitorWidgetRegistry,
      );
      return next.widgets;
    });
  }, []);

  const updateLayout = useCallback((nextWidgets: WidgetPlacement[]) => {
    setWidgets((prev) => {
      const next = gridDashboardReducer(
        { widgets: prev },
        { type: 'UPDATE_LAYOUT', payload: { widgets: nextWidgets } },
        marketMonitorWidgetRegistry,
      );
      return next.widgets;
    });
  }, []);

  const addChartWidgetAt = useCallback(
    ({
      x,
      y,
      seedDsls,
    }: {
      x: number;
      y: number;
      seedDsls?: string[];
    }) => {
      const contract = marketMonitorWidgetRegistry.get(CHART_WIDGET_TYPE);
      if (!contract) return;
      const w = contract.defaultSize.width;
      const h = contract.defaultSize.height;
      const cols = 12;
      const clampedX = Math.max(0, Math.min(cols - w, x));

      setWidgets((prev) => {
        const placedY = bumpDownOnCollision(prev, clampedX, Math.max(0, y), w, h);
        const placement: WidgetPlacement = {
          instanceId: crypto.randomUUID(),
          widgetType: CHART_WIDGET_TYPE,
          x: clampedX,
          y: placedY,
          w,
          h,
          config: createInitialChartConfig(seedDsls) as unknown as Record<string, unknown>,
        };
        return [...prev, placement];
      });
    },
    [],
  );

  const toggleWidgetPin = useCallback((instanceId: string) => {
    setWidgets((prev) =>
      prev.map((w) =>
        w.instanceId === instanceId ? { ...w, pinned: !w.pinned } : w,
      ),
    );
  }, []);

  // Stable identity while the pinned set is unchanged, so controller
  // providers are only rebuilt when a pin actually flips.
  const pinnedKey = useMemo(
    () =>
      widgets
        .filter((w) => w.pinned)
        .map((w) => w.instanceId)
        .sort()
        .join('|'),
    [widgets],
  );
  const pinnedIds = useMemo(
    () => new Set(pinnedKey ? pinnedKey.split('|') : []),
    [pinnedKey],
  );

  const updateChartConfig = useCallback(
    (instanceId: string, mutate: (cfg: ChartWidgetConfig) => ChartWidgetConfig) => {
      setWidgets((prev) =>
        prev.map((w) => {
          if (w.instanceId !== instanceId) return w;
          if (w.widgetType !== CHART_WIDGET_TYPE) return w;
          const current: ChartWidgetConfig = isChartConfig(w.config)
            ? w.config
            : { chartId: '', title: 'Chart', period: '3Y', tags: [] };
          const updated = mutate(current);
          return { ...w, config: updated as unknown as Record<string, unknown> };
        }),
      );
    },
    [],
  );

  const updateExcelConfig = useCallback(
    (instanceId: string, mutate: (cfg: ExcelWidgetConfig) => ExcelWidgetConfig) => {
      setWidgets((prev) =>
        prev.map((w) => {
          if (w.instanceId !== instanceId) return w;
          if (w.widgetType !== EXCEL_WIDGET_TYPE) return w;
          if (!isExcelConfig(w.config)) return w;
          const updated = mutate(w.config as unknown as ExcelWidgetConfig);
          return { ...w, config: updated as unknown as Record<string, unknown> };
        }),
      );
    },
    [],
  );

  const EXCEL_UNDO_LIMIT = 50;
  const undoStacksRef = useRef<Record<string, ExcelWidgetConfig[]>>({});
  const [undoVersions, setUndoVersions] = useState<Record<string, number>>({});

  const bumpUndoVersion = useCallback((instanceId: string) => {
    setUndoVersions((prev) => ({
      ...prev,
      [instanceId]: (prev[instanceId] ?? 0) + 1,
    }));
  }, []);

  const updateExcelConfigUndoable = useCallback(
    (instanceId: string, mutate: (cfg: ExcelWidgetConfig) => ExcelWidgetConfig) => {
      const widget = widgetsRef.current.find((w) => w.instanceId === instanceId);
      if (!widget) return;
      if (widget.widgetType !== EXCEL_WIDGET_TYPE) return;
      if (!isExcelConfig(widget.config)) return;
      const current = widget.config as unknown as ExcelWidgetConfig;
      const updated = mutate(current);
      if (updated === current) return;

      const stack = undoStacksRef.current[instanceId] ?? [];
      const trimmed =
        stack.length >= EXCEL_UNDO_LIMIT
          ? stack.slice(stack.length - EXCEL_UNDO_LIMIT + 1)
          : stack;
      undoStacksRef.current = {
        ...undoStacksRef.current,
        [instanceId]: [...trimmed, current],
      };
      bumpUndoVersion(instanceId);

      setWidgets((prev) =>
        prev.map((w) =>
          w.instanceId === instanceId
            ? { ...w, config: updated as unknown as Record<string, unknown> }
            : w,
        ),
      );
    },
    [bumpUndoVersion],
  );

  const undoExcel = useCallback(
    (instanceId: string) => {
      const stack = undoStacksRef.current[instanceId] ?? [];
      if (stack.length === 0) return;
      const popped = stack[stack.length - 1];
      undoStacksRef.current = {
        ...undoStacksRef.current,
        [instanceId]: stack.slice(0, -1),
      };
      setWidgets((prev) =>
        prev.map((w) =>
          w.instanceId === instanceId
            ? { ...w, config: popped as unknown as Record<string, unknown> }
            : w,
        ),
      );
      bumpUndoVersion(instanceId);
    },
    [bumpUndoVersion],
  );

  const [excelEpochs, setExcelEpochs] = useState<
    Record<string, { refresh: number; retranslate: number }>
  >({});

  const bumpExcelEpoch = useCallback(
    (instanceId: string, kind: 'refresh' | 'retranslate') => {
      setExcelEpochs((prev) => {
        const current = prev[instanceId] ?? { refresh: 0, retranslate: 0 };
        return {
          ...prev,
          [instanceId]: { ...current, [kind]: current[kind] + 1 },
        };
      });
    },
    [],
  );

  const chartControllerProvider: ChartControllerProvider = useMemo(
    () => ({
      getController(instanceId: string): ChartController {
        return {
          setTitle: (title) => {
            updateChartConfig(instanceId, (cfg) => ({ ...cfg, title }));
          },
          setPeriod: (period: ChartPeriod) => {
            updateChartConfig(instanceId, (cfg) => ({ ...cfg, period }));
          },
          setTags: (tags: DslTag[]) => {
            updateChartConfig(instanceId, (cfg) => ({ ...cfg, tags }));
          },
          pinned: pinnedIds.has(instanceId),
          togglePin: () => {
            toggleWidgetPin(instanceId);
          },
          remove: () => {
            removeWidget(instanceId);
          },
        };
      },
    }),
    [updateChartConfig, removeWidget, pinnedIds, toggleWidgetPin],
  );

  const excelControllerProvider: ExcelControllerProvider = useMemo(
    () => ({
      getController(instanceId: string): ExcelController {
        const epochs = excelEpochs[instanceId] ?? { refresh: 0, retranslate: 0 };
        const canUndo = (undoStacksRef.current[instanceId] ?? []).length > 0;
        return {
          setCellRaw: (r, c, raw) => {
            updateExcelConfigUndoable(instanceId, (cfg) =>
              setExcelCellRaw(cfg, r, c, raw),
            );
          },
          setResolvedDsl: (r, c, dsl) => {
            updateExcelConfig(instanceId, (cfg) =>
              setExcelCellResolvedDsl(cfg, r, c, dsl),
            );
          },
          clearCells: (coords) => {
            updateExcelConfigUndoable(instanceId, (cfg) =>
              clearExcelCells(cfg, coords),
            );
          },
          setCellsStyle: (coords, style) => {
            updateExcelConfigUndoable(instanceId, (cfg) =>
              setExcelCellsStyle(cfg, coords, style),
            );
          },
          setCellsFormat: (coords, format) => {
            updateExcelConfigUndoable(instanceId, (cfg) =>
              setExcelCellsFormat(cfg, coords, format),
            );
          },
          setCellsConditionalFormatting: (coords, rules) => {
            updateExcelConfigUndoable(instanceId, (cfg) =>
              setExcelCellsConditionalFormatting(cfg, coords, rules),
            );
          },
          writeSheetCells: (cells) => {
            updateExcelConfigUndoable(instanceId, (cfg) =>
              writeExcelSheetCells(cfg, cells),
            );
          },
          applyCellPatch: (patch, grid) => {
            updateExcelConfigUndoable(instanceId, (cfg) =>
              applyExcelCellPatch(
                grid ? growExcelGrid(cfg, grid.rows, grid.cols) : cfg,
                patch,
              ),
            );
          },
          addRow: () => {
            updateExcelConfigUndoable(instanceId, (cfg) => appendRow(cfg));
          },
          removeRow: () => {
            updateExcelConfigUndoable(instanceId, (cfg) => dropRow(cfg));
          },
          addCol: () => {
            updateExcelConfigUndoable(instanceId, (cfg) => appendCol(cfg));
          },
          removeCol: () => {
            updateExcelConfigUndoable(instanceId, (cfg) => dropCol(cfg));
          },
          requestRefresh: () => bumpExcelEpoch(instanceId, 'refresh'),
          requestRetranslate: () => bumpExcelEpoch(instanceId, 'retranslate'),
          refreshEpoch: epochs.refresh,
          retranslateEpoch: epochs.retranslate,
          chartCells: (coords) => {
            const widget = widgetsRef.current.find(
              (w) => w.instanceId === instanceId,
            );
            if (!widget) return;
            if (!isExcelConfig(widget.config)) return;
            const cfg = widget.config as unknown as ExcelWidgetConfig;
            const seen = new Set<string>();
            const seedDsls: string[] = [];
            for (const { r, c } of coords) {
              if (r < 0 || r >= cfg.rows || c < 0 || c >= cfg.cols) continue;
              const cell = cfg.cells[r][c];
              if (!isDslCell(cell)) continue;
              const seed = chartSeedFor(cfg, r, c);
              if (!seed) continue;
              if (seen.has(seed)) continue;
              seen.add(seed);
              seedDsls.push(seed);
            }
            if (seedDsls.length === 0) return;
            addChartWidgetAt({
              x: widget.x,
              y: widget.y + widget.h,
              seedDsls,
            });
          },
          undo: () => undoExcel(instanceId),
          canUndo,
          pinned: pinnedIds.has(instanceId),
          togglePin: () => {
            toggleWidgetPin(instanceId);
          },
          remove: () => {
            removeWidget(instanceId);
          },
        };
      },
    }),
    [
      updateExcelConfig,
      updateExcelConfigUndoable,
      undoExcel,
      bumpExcelEpoch,
      excelEpochs,
      undoVersions,
      removeWidget,
      addChartWidgetAt,
      pinnedIds,
      toggleWidgetPin,
    ],
  );

  const isDirty = useMemo(
    () => serializeWidgets(widgets) !== savedSnapshot,
    [widgets, savedSnapshot],
  );

  const value: MarketMonitorDashboardState = useMemo(
    () => ({
      list,
      isListLoading,
      activeId,
      activeName,
      widgets,
      isLoadingActive,
      isSaving,
      isDirty,
      error,
      refresh: fetchList,
      open,
      newDashboard,
      saveAs,
      save,
      rename,
      remove,
      addWidget,
      removeWidget,
      updateLayout,
      addChartWidgetAt,
    }),
    [
      list,
      isListLoading,
      activeId,
      activeName,
      widgets,
      isLoadingActive,
      isSaving,
      isDirty,
      error,
      fetchList,
      open,
      newDashboard,
      saveAs,
      save,
      rename,
      remove,
      addWidget,
      removeWidget,
      updateLayout,
      addChartWidgetAt,
    ],
  );

  return (
    <MarketMonitorDashboardContext.Provider value={value}>
      <ChartControllerProviderContext value={chartControllerProvider}>
        <ExcelControllerProviderContext value={excelControllerProvider}>
          {children}
        </ExcelControllerProviderContext>
      </ChartControllerProviderContext>
    </MarketMonitorDashboardContext.Provider>
  );
};

export const useMarketMonitorDashboard = (): MarketMonitorDashboardState => {
  const ctx = useContext(MarketMonitorDashboardContext);
  if (!ctx) {
    throw new Error(
      'useMarketMonitorDashboard must be used inside a MarketMonitorDashboardProvider',
    );
  }
  return ctx;
};
